import React, { useRef, useState } from 'react';
import { RoomQuotationTemplate } from '../utils/RoomQuotationTemplate';
import { Button } from '../../../components/ui/Button';
import { Download, Save, RefreshCw, X, Loader2 } from 'lucide-react';
import html2pdf from 'html2pdf.js';

export const QuotationPreviewModal = ({ data, onClose, onSubmit }) => {
  const pdfRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendToOwner, setSendToOwner] = useState(true);

  const handleSubmit = async () => {
    if (!pdfRef.current || !onSubmit) return;
    
    try {
      setIsSubmitting(true);
      
      const opt = {
        margin: 10,
        filename: `BudgetQuotation_${data.quotationNo || Date.now()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      const pdfBlob = await html2pdf().from(pdfRef.current).set(opt).output('blob');
      
      await onSubmit(pdfBlob, sendToOwner);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-xl shadow-2xl flex flex-col h-[90vh] w-full max-w-5xl overflow-hidden relative">
        
        {/* Header Action Bar */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
          <h2 className="text-lg font-bold text-slate-800">Quotation Preview</h2>
          <div className="flex items-center gap-3">
            {!data?.isInitial && (
              <label className="flex items-center gap-2 mr-2 cursor-not-allowed bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 opacity-80">
                <input 
                  type="checkbox" 
                  checked={sendToOwner}
                  disabled
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue cursor-not-allowed"
                />
                <span className="text-sm font-medium text-slate-700 select-none">Submit to Owner</span>
              </label>
            )}
            <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
              Update Quotation
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              title="Submit quotation"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Generating PDF...
                </>
              ) : (
                'Submit'
              )}
            </Button>
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            <button 
              onClick={onClose}
              disabled={isSubmitting}
              className="p-1.5 rounded hover:bg-red-50 hover:text-red-600 text-slate-500 transition-colors disabled:opacity-50"
              title="Close Preview"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Document Container */}
        <div className="flex-1 overflow-auto p-6 sm:p-10 bg-slate-100 flex justify-center">
          <div className="bg-white shadow-sm ring-1 ring-slate-200 shrink-0" ref={pdfRef}>
            <RoomQuotationTemplate data={{ ...data, rows: data?.rows?.filter(r => !r.isStock && !r._fromStock) || [] }} />
          </div>
        </div>
      </div>
    </div>
  );
};
